import type { PlanAction } from "./plan.types";

/**
 * Where a Home action aimed at cycle detail should actually go.
 *
 * The concept builders are static, so an action that means "this cycle" holds no
 * cycle id — and reading every id-less cycle-detail action as a creation CTA sent
 * "add monitoring data" to the start-a-cycle flow: asked to record today's
 * numbers, the patient was offered to begin treatment instead.
 *
 * A missing id cannot tell the two intents apart on its own. "Start another
 * cycle" is equally id-less and genuinely does want the creation flow. What
 * separates them is whether the action names a section of an existing cycle, so
 * `focus` is the signal and the screen supplies the id it already knows.
 *
 * Extracted from `PlanHomeScreen` so this decision is testable at all: the screen
 * reaches `react-native` and cannot be loaded by the test runner, and the
 * regression that matters — a creation CTA quietly losing its way to
 * start-treatment — is invisible to types and to every other check.
 */
/**
 * A section `CycleDetail` can actually open.
 *
 * `outcome` earns its place the same way the others do: the hub takes a
 * requested step and opens on it (`MoonTreatmentHubCard`'s `requestedStepId`),
 * so an arrival naming it lands somewhere real rather than leaving the screen
 * waiting on a param nothing reads. The sperm-freezing steps `screening`,
 * `sperm_collection` and `analysis`, and the `transfer` step, land the same
 * way: each is a hub step id.
 */
type FocusableSection =
  | "monitoring"
  | "beta"
  | "outcome"
  | "screening"
  | "sperm_collection"
  | "analysis"
  | "transfer";

export type CycleDetailActionTarget =
  /** Open the patient's own open cycle, optionally focused on a section. */
  | { kind: "open-cycle"; cycleId: string; focus?: FocusableSection }
  /** No existing cycle is being named; the creation flow is correct. */
  | { kind: "start-treatment" }
  /** Not a cycle-detail action; this decision does not apply. */
  | { kind: "not-cycle-detail" };

/**
 * The sections an arrival can focus. Narrow on purpose: `focus` reaches here as a
 * loose string from `Record<string, string>`, and a value with no target must
 * arrive at the cycle rather than be forwarded as a param the route cannot honour.
 */
const FOCUSABLE_SECTIONS: ReadonlySet<string> = new Set<FocusableSection>([
  "monitoring",
  "beta",
  "outcome",
  "screening",
  "sperm_collection",
  "analysis",
  "transfer",
]);

/**
 * Whether a `focus` names a section that has somewhere to land.
 *
 * A predicate rather than a boolean so narrowing does the work a cast would
 * otherwise assert: widening `FOCUSABLE_SECTIONS` cannot smuggle an unhandled
 * section into the route's param type without the compiler objecting.
 *
 * Exported so `actionValidation` can share this rule rather than restate it: the
 * validator decides whether an id-less cycle-detail action is legitimate, and the
 * answer is exactly the question this module already answers.
 */
export function isFocusableSection(
  focus: string | undefined,
): focus is FocusableSection {
  return focus !== undefined && FOCUSABLE_SECTIONS.has(focus);
}

export function resolveCycleDetailAction(
  action: Pick<PlanAction, "screen" | "params">,
  treatment: { isInTreatment: boolean; cycleId?: string | null },
): CycleDetailActionTarget {
  const isCycleDetail =
    action.screen === "CycleDetail" || action.screen === "CycleDetailScreen";
  if (!isCycleDetail) return { kind: "not-cycle-detail" };

  // An action that already names its cycle needs no resolving here.
  if (action.params?.cycleId) return { kind: "not-cycle-detail" };

  // Without a focus this is a creation CTA — "Start another cycle" and friends —
  // and must keep reaching the start-treatment flow.
  if (!action.params?.focus) return { kind: "start-treatment" };

  // Only an open cycle is a valid target: `CycleDetail` is the open-cycle route,
  // and a closed cycle belongs on `PastCycleDetail`, which has no form to open.
  // With no cycle at all, asking to add monitoring data can only mean starting
  // one.
  if (!treatment.isInTreatment || !treatment.cycleId) {
    return { kind: "start-treatment" };
  }

  return {
    kind: "open-cycle",
    cycleId: treatment.cycleId,
    // A focus the route cannot honour is dropped rather than forwarded: the
    // arrival still belongs on the cycle, and passing a param no section reads
    // would leave the screen waiting for something to consume it.
    //
    // `logSymptoms` builds `focus: "symptoms"`
    // (`HomepageReviewConceptHome.tsx:726`) as the monitoring widget's
    // `secondaryAction`, but the widget renders only its `primaryAction`, so
    // that one is unreachable rather than merely unhonoured.
    ...(isFocusableSection(action.params.focus)
      ? { focus: action.params.focus }
      : {}),
  };
}
